import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, formatApiError } from "@/api";
import { toast } from "sonner";
import { Plus, X, Trash2, Code, Rss, FileText, Link as LinkIcon } from "lucide-react";

const TYPES = [
  { key: "iframe", label: "Iframe", icon: Code, desc: "URL embedded (site externe)" },
  { key: "rss", label: "Flux RSS", icon: Rss, desc: "Actualités ou blog RSS/Atom" },
  { key: "text", label: "Texte / HTML", icon: FileText, desc: "Note perso, aide-mémoire" },
  { key: "link_list", label: "Liste de liens", icon: LinkIcon, desc: "Raccourcis vers outils" },
];

/**
 * Panneau de gestion des widgets custom (Widget Marketplace).
 * Affiché en haut de la page de settings — permet d'ajouter/supprimer.
 */
export default function CustomWidgetsPanel({ onChange }) {
  const [items, setItems] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ type: "iframe", label: "", size: "m", config: {} });

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get("/chef/custom-widgets");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const resetForm = () => setForm({ type: "iframe", label: "", size: "m", config: {} });

  const submit = async () => {
    if (!form.label.trim()) { toast.error("Nom requis"); return; }
    setSaving(true);
    try {
      await api.post("/chef/custom-widgets", form);
      toast.success("Widget créé");
      setShowForm(false);
      resetForm();
      await load();
      onChange?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (key) => {
    if (!window.confirm("Supprimer ce widget custom ?")) return;
    try {
      await api.delete(`/chef/custom-widgets/${key}`);
      toast.success("Widget supprimé");
      await load();
      onChange?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="bg-white border border-[#DEE2E6] p-4 mb-4" data-testid="custom-widgets-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#495057]">
            Widgets custom (marketplace)
          </div>
          <div className="text-[10px] text-[#868E96] mt-0.5">
            Iframe / RSS / Texte / Liens · limite 10 widgets
          </div>
        </div>
        <Button onClick={() => setShowForm(true)} data-testid="custom-widgets-add" size="sm" className="bg-[#7C3AED] text-white hover:bg-[#6D28D9]">
          <Plus className="h-4 w-4 mr-1" /> Ajouter
        </Button>
      </div>

      {items === null ? (
        <div className="text-xs text-[#868E96]">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-[#868E96] italic">Aucun widget custom pour l'instant.</div>
      ) : (
        <ul className="space-y-1" data-testid="custom-widgets-list">
          {items.map((w) => (
            <li key={w.key} className="flex items-center justify-between p-2 border border-[#DEE2E6] text-xs" data-testid={`custom-widget-${w.key}`}>
              <div className="flex-1 min-w-0">
                <span className="font-semibold">{w.label}</span>
                <span className="ml-2 text-[10px] uppercase tracking-wider text-[#7C3AED] font-bold">{w.type}</span>
                <span className="ml-2 font-mono text-[10px] text-[#868E96]">{w.size}</span>
              </div>
              <button onClick={() => remove(w.key)} data-testid={`custom-widget-remove-${w.key}`} className="p-1 text-[#E60000] hover:bg-[#FEF2F2]">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={showForm} onOpenChange={(o) => { setShowForm(o); if (!o) resetForm(); }}>
        <DialogContent data-testid="custom-widget-form">
          <DialogHeader>
            <DialogTitle>Nouveau widget custom</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Type</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {TYPES.map((t) => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setForm({ ...form, type: t.key, config: {} })}
                      data-testid={`custom-widget-type-${t.key}`}
                      className={`p-3 border text-left transition-colors ${form.type === t.key ? "border-[#7C3AED] bg-[#EDE9FE]" : "border-[#DEE2E6] hover:border-[#0A0A0A]"}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className="h-4 w-4" />
                        <span className="font-display font-bold text-sm">{t.label}</span>
                      </div>
                      <div className="text-[10px] text-[#868E96]">{t.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label htmlFor="cw-label">Nom du widget</Label>
              <Input id="cw-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="custom-widget-label" placeholder="Ex : Actualité PAG" />
            </div>

            <div>
              <Label>Taille par défaut</Label>
              <div className="flex gap-2 mt-2">
                {[
                  { s: "s", l: "Petit" }, { s: "m", l: "Moyen" }, { s: "l", l: "Grand" },
                ].map((opt) => (
                  <button
                    key={opt.s}
                    type="button"
                    onClick={() => setForm({ ...form, size: opt.s })}
                    data-testid={`custom-widget-size-${opt.s}`}
                    className={`px-3 py-1.5 border text-xs uppercase font-bold tracking-wider ${form.size === opt.s ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#DEE2E6]"}`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>

            <ConfigForm type={form.type} config={form.config} onChange={(c) => setForm({ ...form, config: c })} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowForm(false)} data-testid="custom-widget-cancel">Annuler</Button>
            <Button onClick={submit} disabled={saving} data-testid="custom-widget-submit" className="bg-[#7C3AED] text-white">
              {saving ? "Création…" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ConfigForm({ type, config, onChange }) {
  if (type === "iframe") {
    return (
      <>
        <div>
          <Label htmlFor="cw-url">URL iframe (HTTPS recommandé)</Label>
          <Input id="cw-url" value={config.url || ""} onChange={(e) => onChange({ ...config, url: e.target.value })} placeholder="https://example.com/dashboard" data-testid="custom-widget-config-url" />
        </div>
        <div>
          <Label htmlFor="cw-height">Hauteur (px, entre 100 et 600)</Label>
          <Input id="cw-height" type="number" min="100" max="600" value={config.height || 200} onChange={(e) => onChange({ ...config, height: Number(e.target.value) })} data-testid="custom-widget-config-height" />
        </div>
      </>
    );
  }
  if (type === "rss") {
    return (
      <>
        <div>
          <Label htmlFor="cw-feed">URL du flux RSS/Atom</Label>
          <Input id="cw-feed" value={config.feed_url || ""} onChange={(e) => onChange({ ...config, feed_url: e.target.value })} placeholder="https://example.com/rss.xml" data-testid="custom-widget-config-feed" />
        </div>
        <div>
          <Label htmlFor="cw-max">Nombre d'items (1-10)</Label>
          <Input id="cw-max" type="number" min="1" max="10" value={config.max_items || 5} onChange={(e) => onChange({ ...config, max_items: Number(e.target.value) })} data-testid="custom-widget-config-max" />
        </div>
      </>
    );
  }
  if (type === "text") {
    return (
      <div>
        <Label htmlFor="cw-content">Contenu (HTML basique OK, script/iframe filtrés)</Label>
        <Textarea id="cw-content" value={config.content || ""} onChange={(e) => onChange({ ...config, content: e.target.value })} rows={6} data-testid="custom-widget-config-content" />
      </div>
    );
  }
  if (type === "link_list") {
    const links = config.links || [];
    const setLink = (i, patch) => {
      const next = [...links];
      next[i] = { ...next[i], ...patch };
      onChange({ ...config, links: next });
    };
    return (
      <div>
        <Label>Liens ({links.length}/20)</Label>
        <div className="space-y-2 mt-2">
          {links.map((l, i) => (
            <div key={i} className="flex gap-2 items-center" data-testid={`custom-widget-link-${i}`}>
              <Input value={l.label || ""} onChange={(e) => setLink(i, { label: e.target.value })} placeholder="Label" className="flex-1" />
              <Input value={l.url || ""} onChange={(e) => setLink(i, { url: e.target.value })} placeholder="https://..." className="flex-1" />
              <button type="button" onClick={() => onChange({ ...config, links: links.filter((_, j) => j !== i) })} className="p-1 text-[#E60000]">
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
          <Button type="button" size="sm" variant="outline" onClick={() => onChange({ ...config, links: [...links, { label: "", url: "" }] })} data-testid="custom-widget-add-link" disabled={links.length >= 20}>
            <Plus className="h-3 w-3 mr-1" /> Ajouter un lien
          </Button>
        </div>
      </div>
    );
  }
  return null;
}
